import { FormControl, FormGroup } from '@angular/forms';
import {
  CustomCodeFormShape,
  CustomCodeVariableDoc,
  createCustomCodeFormGroup,
} from '../custom-code/custom-code.model';

export interface CartSubtotalFormShape {
  singleItemLabel: FormControl<string>;
  multipleItemLabel: FormControl<string>;
  customCode: FormGroup<CustomCodeFormShape>;
}

const DEFAULTS = {
  singleItemLabel: 'Subtotal ({item_count} item)',
  multipleItemLabel: 'Subtotal ({item_count} items)',
};

export const CART_SUBTOTAL_VARIABLES: CustomCodeVariableDoc[] = [
  {
    reference: '{{componentId}}',
    type: 'String',
    description: 'Unique widget ID, useful as a DOM hook for your own CSS or JS',
  },
  {
    reference: '{{label}}',
    type: 'String',
    description:
      'Configured subtotal label, picked between single/multiple based on cart count, with item count already substituted',
  },
  { reference: '{{itemCount}}', type: 'Number', description: 'Current number of items in the cart' },
  { reference: '{{totalPrice}}', type: 'String', description: 'Cart subtotal formatted with currency (e.g. "$24.99")' },
  {
    reference: '{{originalTotalPrice}}',
    type: 'String',
    description: 'Original price before discounts, formatted with currency',
  },
  {
    reference: '{{discountAmount}}',
    type: 'String',
    description: 'Total savings, formatted with currency (e.g. "$5.00") — useful for "You saved" messaging',
  },
  {
    reference: '{{hasDiscount}}',
    type: 'Boolean',
    description: 'True when a discount is active (original total is higher than current)',
  },
];

export const CART_SUBTOTAL_STARTER_TEMPLATE = `<div
  style="display: flex; justify-content: space-between; align-items: center; padding: 12px 20px; font-size: 14px; font-weight: 700;"
>
  <span>{{label}}</span>
  <div style="display: flex; align-items: center; gap: 8px;">
    <span>{{totalPrice}}</span>
    {{#if hasDiscount}}
      <span style="color: #999; text-decoration: line-through; font-weight: 400;">{{originalTotalPrice}}</span>
    {{/if}}
  </div>
</div>`;

export function createCartSubtotalFormGroup(
  initialSettings?: Record<string, unknown> | null,
): FormGroup<CartSubtotalFormShape> {
  const formGroup = new FormGroup<CartSubtotalFormShape>({
    singleItemLabel: new FormControl(DEFAULTS.singleItemLabel, { nonNullable: true }),
    multipleItemLabel: new FormControl(DEFAULTS.multipleItemLabel, { nonNullable: true }),
    customCode: createCustomCodeFormGroup(undefined, CART_SUBTOTAL_STARTER_TEMPLATE),
  });

  if (initialSettings) {
    formGroup.patchValue(initialSettings, { emitEvent: false });
  }

  return formGroup;
}
